import {ChangeDetectionStrategy, Component, inject, LOCALE_ID, signal} from '@angular/core';
import {APP_BASE_HREF, AsyncPipe, NgOptimizedImage} from "@angular/common";
import {UserService} from "./service/user.service";
import {NavigationEnd, Params, Router, RouterLink, RouterOutlet} from "@angular/router";
import {map, Observable} from "rxjs";
import {filter} from "rxjs/operators";
import {FooterComponent} from './components/common/footer/footer.component';
import {ToastsContainer} from './components/common/toasts-container.component';
import {NgbCollapse, NgbNav, NgbNavItem, NgbNavLink, NgbNavLinkBase} from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.sass'],
  imports: [
    NgOptimizedImage,
    NgbCollapse,
    NgbNav,
    NgbNavItem,
    NgbNavLink,
    NgbNavLinkBase,
    RouterLink,
    ToastsContainer,
    RouterOutlet,
    FooterComponent,
    AsyncPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent {
  baseHref = inject(APP_BASE_HREF);
  locale = inject(LOCALE_ID);
  userService = inject(UserService);

  themeAttribute = document.documentElement.attributes.getNamedItem('data-bs-theme');
  title = 'grant-application';

  links: {
    title: string,
    fragment: string,
    queryParams?: Params,
    canActivate: boolean | (() => boolean),
    requireLogin: boolean
  }[] = [
    {
      title: $localize`Strona startowa`,
      fragment: '/',
      canActivate: true,
      requireLogin: false
    },
    {
      title: $localize`Lista wniosków`,
      fragment: '/application',
      canActivate: true,
      requireLogin: true,
      queryParams: {
        sort: 'applicationDate',
        sortDir: 'desc',
        byApplicant: 'false',
        dateFrom: new Date(new Date().getFullYear(), 0, 1).toLocaleDateString("sv")
      }
    },
    {
      title: $localize`Zadania`,
      fragment: '/application/task',
      canActivate: true,
      requireLogin: true,
      queryParams: {
        sort: 'application.applicationDate',
        sortDir: 'desc',
        taskStatus: ['IN_PROGRESS', 'WITHDRAWN_FOR_CORRECTION']
      }
    },
    {
      title: $localize`Akceptacje`,
      fragment: '/application/accept',
      canActivate: true,
      requireLogin: true,
      queryParams: {
        sort: 'application.applicationDate',
        sortDir: 'desc',
        toAccept: 'true',
        myAcceptances: 'true',
        accepted: 'false'
      }
    },
    {
      title: $localize`Dodatkowe informacje o wnioskach`,
      fragment: '/application/additionalInfo',
      canActivate: () => this.userService.hasAnyRole('ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_ADDITIONAL_INFO'),
      requireLogin: true,
      queryParams: {
        sort: 'applicationDate',
        sortDir: 'desc',
        byApplicant: 'false',
        dateFrom: new Date(new Date().getFullYear(), 0, 1).toLocaleDateString("sv")
      }
    },
    {
      title: $localize`Rankingi`,
      fragment: '/application/ranking',
      canActivate: () => this.userService.hasAnyRole('ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_UFS_COMMITTEE_MEMBER'),
      requireLogin: true
    },
    {
      title: $localize`Panel administratora`,
      fragment: '/admin',
      canActivate: () => this.userService.hasAnyRole('ROLE_SUPER_ADMIN', 'ROLE_ADMIN'),
      requireLogin: true
    },
  ];

  isNavBarCollapsed = signal(true);

  $activeRoute: Observable<string | null>

  constructor() {
    const router = inject(Router);

    if (this.themeAttribute) {
      this.themeAttribute.value = localStorage.getItem('theme') ?? 'light';
    }
    this.$activeRoute = router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(event => (event as NavigationEnd).url),
      map(url => [...this.links]
        .sort((a, b) => b.fragment.length - a.fragment.length)
        .find(link => url?.includes(link.fragment))?.fragment ?? null
      )
    )
  }

  getServiceUrl() {
    return this.baseHref//+ this.location.path().slice(1, this.location.path().length);
  }

  exitImpersonate() {
    this.userService.exitImpersonate().subscribe();
  }

  canActivate(canActivate: boolean | (() => boolean)) {
    return typeof canActivate === 'boolean' ? canActivate : canActivate();
  }

  getTheme() {
    return this.themeAttribute?.value ?? 'light';
  }

  toggleDarkMode() {
    if (this.themeAttribute) {
      const newTheme = this.themeAttribute.value === 'dark' ? 'light' : 'dark';
      localStorage.setItem('theme', newTheme);
      this.themeAttribute.value = newTheme;
    }
  }
}
